<script setup>
import apiClient from "@/apiClient.js";
import {
  ArrowPathIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";
import dayjs from "dayjs";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const locations = ref([]);
const meta = ref({});
const activeLocation = ref(null);
const loading = ref(false);
const error = ref("");
const center = ref({ lat: 53.2487471, lng: -3.0048407 });
const zoom = ref(7);

const markers = computed(() => {
  const seen = new Set();

  return locations.value
    .map((location) => {
      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);

      return {
        ...location,
        position: { lat: latitude, lng: longitude },
      };
    })
    .filter((location) => {
      if (!Number.isFinite(location.position.lat) || !Number.isFinite(location.position.lng)) {
        return false;
      }

      const key = `${location.position.lat.toFixed(6)},${location.position.lng.toFixed(6)}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
});

const eventCount = computed(() =>
  Number(meta.value?.event_count ?? markers.value.reduce((total, location) => total + Number(location.event_count ?? 0), 0))
);

const locationLabel = (location) =>
  location?.location_name || location?.location_address || "Job location";

const selectLocation = (location) => {
  activeLocation.value = location;
};

const closeLocation = () => {
  activeLocation.value = null;
};

const formatDate = (value) => {
  if (!value) {
    return "No date";
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
};

const dateRange = (location) => {
  const first = formatDate(location?.first_event_date);
  const last = formatDate(location?.last_event_date);

  return first === last ? first : `${first} - ${last}`;
};

const hiddenReferenceCount = (location) =>
  Math.max(
    Number(location?.reference_count ?? 0) - Number(location?.reference_summaries?.length ?? 0),
    0
  );

const viewReference = (reference) => {
  if (!reference?.id) {
    return;
  }

  router.push({
    name: "reference-view",
    params: { id: reference.id },
  });
};

const loadLocations = async () => {
  loading.value = true;
  error.value = "";

  try {
    const response = await apiClient.get("/api/job-map/locations");
    locations.value = Array.isArray(response.data?.data) ? response.data.data : [];
    meta.value = response.data?.meta ?? {};
  } catch (err) {
    error.value = err?.response?.data?.message ?? "Job map locations unavailable.";
    console.error("Failed to fetch job map locations:", err);
  } finally {
    loading.value = false;
  }
};

onMounted(loadLocations);
</script>

<template>
  <div class="job-map">
    <header class="job-map-toolbar">
      <div class="min-w-0">
        <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Job Map</h1>
        <div class="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span class="inline-flex items-center gap-1">
            <MapPinIcon class="h-4 w-4" />
            {{ markers.length }} locations
          </span>
          <span class="inline-flex items-center gap-1">
            <BriefcaseIcon class="h-4 w-4" />
            {{ eventCount }} events
          </span>
          <span v-if="meta.refreshed_at" class="inline-flex items-center gap-1">
            <CalendarDaysIcon class="h-4 w-4" />
            {{ formatDate(meta.refreshed_at) }}
          </span>
        </div>
      </div>
      <button class="btn flex h-9 items-center" type="button" :disabled="loading" @click="loadLocations">
        <ArrowPathIcon class="mr-1 h-4 w-4" />
        <span>{{ loading ? "Loading" : "Refresh" }}</span>
      </button>
    </header>

    <div class="job-map-frame">
      <GMapMap :center="center" :zoom="zoom" map-type-id="terrain" class="job-map-canvas">
        <GMapMarker
          v-for="location in markers"
          :key="location.dedupe_key || location.id"
          :position="location.position"
          :title="locationLabel(location)"
          @click="selectLocation(location)"
        />
      </GMapMap>

      <div v-if="loading && !markers.length" class="job-map-state">
        Loading job map
      </div>
      <div v-else-if="error" class="job-map-state text-red-700 dark:text-red-200">
        {{ error }}
      </div>
      <div v-else-if="!markers.length" class="job-map-state">
        No mapped job locations
      </div>

      <aside v-if="activeLocation" class="job-map-panel">
        <div class="flex items-start justify-between gap-3 border-b border-slate-200 p-4 dark:border-slate-700">
          <div class="min-w-0">
            <h2 class="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {{ locationLabel(activeLocation) }}
            </h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {{ activeLocation.location_address }}
            </p>
          </div>
          <button class="btn h-8 w-8 p-1" type="button" title="Close" @click="closeLocation">
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>

        <div class="grid grid-cols-3 gap-px border-b border-slate-200 bg-slate-200 text-center dark:border-slate-700 dark:bg-slate-700">
          <div class="bg-white p-3 dark:bg-slate-900">
            <div class="text-lg font-semibold text-slate-900 dark:text-slate-100">{{ activeLocation.event_count }}</div>
            <div class="text-[11px] uppercase text-slate-500">Events</div>
          </div>
          <div class="bg-white p-3 dark:bg-slate-900">
            <div class="text-lg font-semibold text-slate-900 dark:text-slate-100">{{ activeLocation.reference_count }}</div>
            <div class="text-[11px] uppercase text-slate-500">Jobs</div>
          </div>
          <div class="bg-white p-3 dark:bg-slate-900">
            <div class="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{{ dateRange(activeLocation) }}</div>
            <div class="text-[11px] uppercase text-slate-500">Dates</div>
          </div>
        </div>

        <div class="max-h-96 overflow-y-auto">
          <button
            v-for="reference in activeLocation.reference_summaries || []"
            :key="reference.id"
            type="button"
            class="block w-full border-b border-slate-100 p-4 text-left hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
            @click="viewReference(reference)"
          >
            <div class="flex items-center justify-between gap-3">
              <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ reference.txt }}</span>
              <span class="text-xs text-slate-500">{{ formatDate(reference.next_event_date || reference.first_event_date) }}</span>
            </div>
            <div class="mt-1 truncate text-sm text-slate-700 dark:text-slate-300">{{ reference.name }}</div>
            <div v-if="reference.customer" class="mt-1 truncate text-xs text-slate-500">{{ reference.customer }}</div>
          </button>
          <div
            v-if="hiddenReferenceCount(activeLocation)"
            class="p-4 text-sm font-semibold text-slate-500 dark:text-slate-400"
          >
            {{ hiddenReferenceCount(activeLocation) }} more jobs at this location
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.job-map {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 6em);
  min-height: 32rem;
  width: 100%;
}

.job-map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgb(226 232 240);
  padding: 0.75rem 1rem;
}

.job-map-frame {
  position: relative;
  min-height: 0;
  flex: 1;
}

.job-map-canvas {
  height: 100%;
  width: 100%;
}

.job-map-state {
  position: absolute;
  left: 1rem;
  top: 1rem;
  max-width: min(24rem, calc(100% - 2rem));
  border: 1px solid rgb(226 232 240);
  background: rgb(255 255 255 / 0.95);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(51 65 85);
  box-shadow: 0 10px 25px rgb(15 23 42 / 0.12);
}

.job-map-panel {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  top: 1rem;
  width: min(26rem, calc(100% - 2rem));
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  background: rgb(255 255 255 / 0.97);
  box-shadow: 0 20px 40px rgb(15 23 42 / 0.18);
}

@media (max-width: 640px) {
  .job-map {
    height: calc(100vh - 5em);
  }

  .job-map-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .job-map-panel {
    left: 0.75rem;
    right: 0.75rem;
    top: auto;
    width: auto;
    max-height: 65%;
  }
}
</style>
